import queryString from 'query-string';
import { captureErrorMessage } from './error-reporting';

export type MarketingAttributes = ReferrerAttributes & {
  isMarketingEvent: true;
  writeKey: 'identity';
  page_canonical_url: string;
  page_url: string;
  page_domain: string;
  page_subdomain: string;
  page_path: string;
  page_url_parameters?: string;
  page_url_params_json?: string;
};

type ReferrerAttributes = {
  page_referrer_url?: string;
  page_referrer_path?: string;
  page_referrer_domain?: string;
  page_referrer_subdomain?: string;
};

// Marketing attributes to extract from the URL from which the marketing event is fired.
// List compiled by Atlassian Marketing Engineering here:
// https://hello.atlassian.net/wiki/spaces/GTMDE/pages/1357221110/Analysis+Trello+hybrid+pages+and+UGC
// Code adapted from: http://go/bb/trello/web/src/1d8d61/packages/atlassian-analytics/src/getMarketingAttributes.ts
export const MARKETING_QUERY_PARAM_NAMES = [
  // utm fields
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  // marketing identifiers
  'fbclid',
  'gclid',
  'gclsrc',
  'msclkid',
  // marketing fields
  'adposition',
  'adgroup',
  'campaign',
  'creative',
  'device',
  'keyword',
  'matchtype',
  'network',
  'placement',
];

export const sanitiseSearchParams = (search: string): string => {
  const searchParams: Record<string, string> = queryString.parse(search);
  const filteredParams = Object.entries(searchParams).filter(([key]) =>
    MARKETING_QUERY_PARAM_NAMES.includes(key)
  );
  return queryString.stringify(Object.fromEntries(filteredParams));
};

const extractReferrerAttributes = (referrer: string): ReferrerAttributes | undefined => {
  if (!referrer) return;

  const referrerUrl = new URL(referrer);

  return {
    page_referrer_url: referrerUrl.origin + referrerUrl.pathname,
    page_referrer_path: referrerUrl.pathname,
    page_referrer_domain: referrerUrl.hostname,
    page_referrer_subdomain: referrerUrl.hostname, // same as domain, will be transformed in MarketingEng pipeline
  };
};

/**
 * Returns marketing attributes for URLs without PII, UGC or security sensitive data
 * Analytics events with `{isMarketingEvent: true}` will be sent to marketing
 */
export const getMarketingAttributes = (): MarketingAttributes | {} => {
  try {
    const { location } = window;

    const urlSearchParams = sanitiseSearchParams(location.search);
    const sanitisedSearchParams = urlSearchParams && `?${urlSearchParams}`;

    return {
      writeKey: 'identity',
      isMarketingEvent: true,
      page_path: location.pathname,
      page_domain: location.hostname,
      page_subdomain: location.hostname, // same as domain, will be transformed in MarketingEng pipeline
      page_canonical_url: location.origin + location.pathname + sanitisedSearchParams,
      page_url: location.origin + location.pathname + sanitisedSearchParams,
      ...(sanitisedSearchParams && {
        page_url_parameters: sanitisedSearchParams,
        page_url_params_json: JSON.stringify(queryString.parse(urlSearchParams)),
      }),
      ...extractReferrerAttributes(document.referrer),
    };
  } catch (err) {
    captureErrorMessage('getMarketingAttributes', err);
    return {};
  }
};
